import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { listAlerts } from '@/api/observe';
import AlertItem from '@/components/AlertItem';
import Empty from '@/components/Empty';
import { cn } from '@/lib/cn';
import type { AlertSeverity } from '@/types';

const SEV_FILTERS: Array<AlertSeverity | 'all'> = ['all', 'critical', 'error', 'warn', 'info'];

export default function Alerts() {
  const [severity, setSeverity] = useState<AlertSeverity | 'all'>('all');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['alerts', { severity }],
    queryFn: () => listAlerts({ limit: 200, severity: severity === 'all' ? undefined : severity }),
    refetchInterval: 3_000,    // hot polling on this page
  });

  const alerts = data?.alerts ?? [];

  return (
    <div className="space-y-4 max-w-5xl">
      <header className="flex items-start sm:items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold">Alerts</h1>
          <p className="text-sm text-slate-500 mt-1">Live feed · refreshes every 3s</p>
        </div>

        <div className="flex items-center gap-1 bg-slate-900/60 border border-slate-800 rounded-md p-0.5 overflow-x-auto max-w-full">
          {SEV_FILTERS.map(s => (
            <button
              key={s}
              onClick={() => setSeverity(s)}
              className={cn(
                'px-2.5 sm:px-3 py-1 text-xs font-medium rounded transition shrink-0',
                severity === s
                  ? 'bg-slate-800 text-white'
                  : 'text-slate-400 hover:text-white',
              )}
            >
              {s}
            </button>
          ))}
        </div>
      </header>

      {isLoading && <div className="text-sm text-slate-500">Loading alerts…</div>}
      {isError && <div className="text-sm text-red-300">Failed to load alerts.</div>}
      {!isLoading && !isError && alerts.length === 0 && (
        <Empty
          icon={<Bell className="w-8 h-8" />}
          title="No alerts"
          hint="Nothing has fired yet. Try filtering or wait for the next event."
        />
      )}

      <div className="space-y-1">
        {alerts.map(a => <AlertItem key={a.id} alert={a} />)}
      </div>
    </div>
  );
}
